import { useEffect, useMemo } from 'react'

import {
  useDocumentRetrieval,
  useDocumentStore,
  useDocumentVersion,
} from '@/features/document'
import { ResponseDocumentVersion } from '@/schema'

import { TFLUtils } from '../mappers'
import { useTflViewerStore } from '../store'
import { TflDocumentMetadata, TflMeta } from '../types'

import { useTflHtml } from './hooks'

/**
 * Return type for useDocumentViewer hook
 */
export interface DocumentViewerResult {
  isLoading: boolean
  isError: boolean
  error: Error | null
  refetch: () => void
  versionList: ResponseDocumentVersion[]
  currentVersionId: string | null
}

/**
 * Custom hook for syncing metadata to Zustand store
 *
 * Separated for better testability and single responsibility.
 */
const useSyncMetadataToStore = (
  fileId: string | undefined,
  metadata: TflDocumentMetadata | undefined
) => {
  const setDocumentMetadata = useTflViewerStore(
    (state) => state.setDocumentMetadata
  )

  useEffect(() => {
    if (metadata && fileId) {
      const mappedMetadata = TFLUtils.mappingFromDetailMetadata(metadata)
      setDocumentMetadata(metadata.document_id, mappedMetadata)
    }
  }, [metadata, fileId, setDocumentMetadata])
}

const useSyncHtmlToStore = (
  fileId: string | undefined,
  html: string | undefined
) => {
  const setHtmlSnapshot = useTflViewerStore((state) => state.setHtmlSnapshot)

  useEffect(() => {
    if (html && fileId) {
      setHtmlSnapshot(fileId, html)
    }
  }, [html, fileId, setHtmlSnapshot])
}

export const useDocumentViewer = (
  fileId?: string,
  versionIdProp?: string
): DocumentViewerResult => {
  const { versionIdByDocumentId, getListVersion } = useDocumentStore()

  const currentVersionId = fileId ? versionIdByDocumentId[fileId] : null
  const effectiveVersionId = versionIdProp || currentVersionId
  const shouldUseVersionFetch = !!effectiveVersionId && !!fileId

  // Fetch using document retrieval (for backward compatibility)
  const documentRetrievalQuery = useDocumentRetrieval(
    shouldUseVersionFetch ? '' : fileId || ''
  )

  // Fetch using document version (when versionId is provided)
  const documentVersionQuery = useDocumentVersion(
    shouldUseVersionFetch ? fileId || '' : '',
    shouldUseVersionFetch ? effectiveVersionId || '' : ''
  )

  // Use the appropriate query based on which one is enabled
  const {
    data: metadata,
    isLoading: isLoadingMetadata,
    isError: isErrorMetadata,
    error: errorMetadata,
    refetch: refetchMetadata,
  } = shouldUseVersionFetch ? documentVersionQuery : documentRetrievalQuery
  // Step 3: Extract signed URL for HTML fetch
  const signedUrl = useMemo(
    () =>
      metadata
        ? TFLUtils.mappingFromDetailMetadata(
            metadata as unknown as TflDocumentMetadata
          ).signedUrl
        : undefined,
    [metadata]
  )

  // Step 4: Fetch HTML using signed URL
  const htmlQuery = useTflHtml(signedUrl, fileId, versionIdProp)
  // Step 5: Sync metadata to store (when available)
  useSyncMetadataToStore(fileId, metadata as unknown as TflDocumentMetadata)

  // Step 6: Sync HTML to store (when available)
  useSyncHtmlToStore(fileId, htmlQuery.data)

  // Step 7: Compute combined states
  const isLoading = useMemo(
    () => isLoadingMetadata || htmlQuery.isLoading,
    [isLoadingMetadata, htmlQuery.isLoading]
  )
  const isError = useMemo(
    () => isErrorMetadata || htmlQuery.isError,
    [isErrorMetadata, htmlQuery.isError]
  )
  const error = useMemo(
    () => errorMetadata || htmlQuery.error || null,
    [errorMetadata, htmlQuery.error]
  )

  const refetch = useMemo(
    () => () => {
      refetchMetadata()
      htmlQuery.refetch()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [refetchMetadata, htmlQuery.refetch]
  )

  const currentListVersion = fileId ? getListVersion(fileId) : []

  return {
    isLoading,
    isError,
    error,
    refetch,
    versionList: currentListVersion,
    currentVersionId,
  }
}

export const useDocumentMeta = (
  fileId?: string
): Partial<TflMeta> | undefined => {
  const metadataByDocId = useTflViewerStore((state) => state.metadataByDocId)

  if (!fileId) return undefined

  return metadataByDocId[fileId]
}

export const useDocumentHtml = (fileId?: string): string | undefined => {
  const lastHtmlByFileId = useTflViewerStore((state) => state.lastHtmlByFileId)
  return fileId ? lastHtmlByFileId[fileId] : undefined
}
